import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { SITE_URLS } from '@constants';
import { Form } from '@components/atoms/Form';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputTypes } from '@components/atoms/Input';
import { Typography } from '@components/atoms/Typography';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Table } from '@components/atoms/Table';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useEditSchedule } from '@services/useEditSchedule';
import { InputErrors } from '@types';
import { useGetMyAccountSchedule } from '@services/useGetMyAccountSchedule';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';

type ScheduleFormErrors = Record<Day, InputErrors> & { general: string };

const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
type Day = (typeof daysOfWeek)[number];

type Interval = { from: string; to: string };
type DayState = { checked: boolean; intervals: Interval[] };
type ScheduleState = Record<Day, DayState>;

export const ManageSchedule: FC = () => {
    const { navigate } = useCustomNavigate();
    const { uuid } = useParams();
    const { can } = usePermissionChecker();
    const { showDefaultNotification } = useNotifications();
    const { login, allSettings, myAccountStoreSchedule, isLoadingMyAccountStoreSchedule } = useData();
    const {
        request: requestEditSchedule,
        errorMessage,
        validationErrors,
        success: successMessage,
        lastResponseTime,
    } = useEditSchedule();
    const { request: requestSchedule } = useGetMyAccountSchedule();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-schedule-component.title'),
        intervalRequiredError: t('manage-schedule-component.interval.required-error'),
        to: t('manage-schedule-component.interval.to'),
        addInterval: t('manage-schedule-component.interval.add'),
        removeInterval: t('manage-schedule-component.interval.remove'),
        submitButton: t('manage-schedule-component.submit.button'),
        monday: t('week-day.monday'),
        tuesday: t('week-day.tuesday'),
        wednesday: t('week-day.wednesday'),
        thursday: t('week-day.thursday'),
        friday: t('week-day.friday'),
        saturday: t('week-day.saturday'),
        sunday: t('week-day.sunday'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (
            can(UserPermissions.accessMerchantAreas) === false ||
            // inactive schedule module
            allSettings?.data.module_services?.schedule_management === false
        ) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const emptyInterval: Interval = { from: '', to: '' };
    const formRef = useRef<HTMLFormElement>(null);

    const [scheduleState, setScheduleState] = useState<ScheduleState>(() => {
        const initialState = {} as ScheduleState;
        daysOfWeek.forEach((day) => {
            initialState[day] = { checked: false, intervals: [] };
        });
        return initialState;
    });

    const [errors, setErrors] = useState<ScheduleFormErrors>({
        general: '',
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
    });

    const setDayState = (day: Day, update: Partial<DayState>) => {
        setScheduleState((prev) => ({
            ...prev,
            [day]: { ...prev[day], ...update },
        }));
    };

    useEffect(() => {
        requestSchedule({ uuid: uuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!myAccountStoreSchedule?.data?.weekly_schedule) return;

        const newState = { ...scheduleState };
        daysOfWeek.forEach((day) => {
            const intervals = myAccountStoreSchedule.data.weekly_schedule?.[day] || [];
            newState[day] = {
                checked: intervals.length > 0,
                intervals: intervals.map((i) => ({ from: i.from || '', to: i.to || '' })),
            };
        });
        setScheduleState(newState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountStoreSchedule]);

    const validateForm = (): ScheduleFormErrors => {
        const newErrors = { general: '', ...Object.fromEntries(daysOfWeek.map((day) => [day, []])) } as ScheduleFormErrors;

        daysOfWeek.forEach((day) => {
            scheduleState[day].intervals.forEach(({ from, to }) => {
                if (!from.trim() || !to.trim()) {
                    newErrors[day].push(translations.intervalRequiredError);
                }
            });
        });

        setErrors(newErrors);
        return newErrors;
    };

    const handleOnClickSaveWorker = () => {
        const formErrors = validateForm();
        const hasErrors = daysOfWeek.some((day) => formErrors[day].length > 0);
        if (!hasErrors) {
            formRef.current?.requestSubmit();
        }
    };

    const submitForm = async () => {
        requestEditSchedule(
            { uuid: uuid || '' },
            {
                intervals: Object.fromEntries(daysOfWeek.map((day) => [day, scheduleState[day].intervals])) as Record<
                    Day,
                    Interval[]
                >,
            }
        );
    };

    useEffect(() => {
        if (!lastResponseTime) return;
        if (errorMessage || validationErrors) {
            setErrors({
                general: errorMessage || '',
                ...daysOfWeek.reduce(
                    (acc, day) => {
                        acc[day] = validationErrors?.[day] || [];
                        return acc;
                    },
                    {} as Record<Day, string[]>
                ),
            });
        } else if (login) {
            showDefaultNotification({ type: 'success', message: successMessage || '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnAddInterval = (day: Day) => {
        setDayState(day, {
            intervals: [...scheduleState[day].intervals, emptyInterval],
        });
    };

    const handleOnRemoveInterval = (day: Day, index: number) => {
        setDayState(day, {
            intervals: scheduleState[day].intervals.filter((_, idx) => idx !== index),
        });
    };

    const handleOnChangeIntervalFrom = (day: Day, index: number, value: string) => {
        setDayState(day, {
            intervals: scheduleState[day].intervals.map((interval, idx) =>
                idx === index ? { ...interval, from: value } : interval
            ),
        });
    };

    const handleOnChangeIntervalTo = (day: Day, index: number, value: string) => {
        setDayState(day, {
            intervals: scheduleState[day].intervals.map((interval, idx) =>
                idx === index ? { ...interval, to: value } : interval
            ),
        });
    };

    return (
        <MyAccountContainer title={translations.title}>
            <div className="pt-4">
                <Form formRef={formRef} onSubmit={submitForm}>
                    <>
                        {errors.general && (
                            <>
                                <ErrorMessages messages={[errors.general]} />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </>
                        )}
                    </>

                    <div className="row">
                        <Table
                            contentRows={daysOfWeek.map((day) => [
                                {
                                    classes: 'align-content-start',
                                    content: (
                                        <div className="d-flex flex-grow-1 flex-column">
                                            <Checkbox
                                                type={CheckboxType.normal}
                                                name={day}
                                                value="open"
                                                checked={scheduleState[day].checked}
                                                onChange={() => {
                                                    const checked = !scheduleState[day].checked;
                                                    setDayState(day, { checked, intervals: checked ? [emptyInterval] : [] });
                                                }}
                                                label={<Typography>{translations[day]}</Typography>}
                                            />
                                            {errors[day].length > 0 && <ErrorMessages messages={errors[day]} />}
                                        </div>
                                    ),
                                },
                                {
                                    content: (
                                        <div className="intervals flex-grow-1 ps-4">
                                            <>
                                                {!isLoadingMyAccountStoreSchedule &&
                                                    scheduleState[day].intervals?.map((interval, index) => (
                                                        <div key={`interval-${index}`} className="d-flex interval pb-3">
                                                            <div className="flex-grow-1">
                                                                <div className="form-group">
                                                                    <Input
                                                                        type={InputTypes.time}
                                                                        value={interval.from}
                                                                        onChange={(value) =>
                                                                            handleOnChangeIntervalFrom(day, index, value)
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="align-content-center text-muted px-2">
                                                                <small>{translations.to}</small>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="form-group">
                                                                    <Input
                                                                        type={InputTypes.time}
                                                                        value={interval.to}
                                                                        onChange={(value) =>
                                                                            handleOnChangeIntervalTo(day, index, value)
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="align-content-center px-3">
                                                                <Button
                                                                    type="button"
                                                                    mode={ButtonMode.danger}
                                                                    label={translations.removeInterval}
                                                                    icon="fa-light fa-trash"
                                                                    onClick={() => handleOnRemoveInterval(day, index)}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                <>
                                                    {scheduleState[day].intervals.length > 0 && (
                                                        <Button
                                                            type="button"
                                                            mode={ButtonMode.link}
                                                            label={translations.addInterval}
                                                            onClick={() => handleOnAddInterval(day)}
                                                        />
                                                    )}
                                                </>
                                            </>
                                        </div>
                                    ),
                                },
                            ])}
                        />
                    </div>

                    <div className="col-12 text-end">
                        <Button
                            icon="fa-solid fa-floppy-disk"
                            label={translations.submitButton}
                            mode={ButtonMode.primary}
                            disabled={isLoadingMyAccountStoreSchedule}
                            onClick={handleOnClickSaveWorker}
                        />
                    </div>
                </Form>
            </div>
        </MyAccountContainer>
    );
};
